import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { useActiveSession } from '../api/active/sessionProvider';
import { canUseCatalogAction } from '../api/formal/phase1Permissions';
import type { CatalogMediaApi } from '../catalog-media/catalogMediaApi';
import {
  type PackagePageRuntime,
  usePackagePageRuntime,
} from '../package-integration/packagePageRuntime';
import { createPlanApi } from './planApi';
import type { PlanPort } from './planPort';

export type PlanPageRuntime = {
  api: PlanPort;
  packageApi: PackagePageRuntime['api'];
  equipmentApi: PackagePageRuntime['equipmentApi'];
  planMembership: NonNullable<PackagePageRuntime['planMembership']>;
  media: CatalogMediaApi;
  canRead: boolean;
  canReadPackages?: boolean;
  canReadEquipment?: boolean;
  canManage: boolean;
  canCreate?: boolean;
  canSetStatus?: boolean;
  /** 阶段一：删除只允许管理员部门，服务端仍会再次校验。 */
  canDelete: boolean;
  recoverUnauthorized(): Promise<void>;
};

const Context = createContext<PlanPageRuntime | null>(null);

export function PlanPageRuntimeProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: PlanPageRuntime;
}) {
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function ActivePlanPageRuntimeProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { state, transport, refresh } = useActiveSession();
  const packageRuntime = usePackagePageRuntime();
  const planMembership = packageRuntime.planMembership;
  const ready = state.status === 'ready' ? state : null;
  const value = useMemo<PlanPageRuntime>(
    () => ({
      api: createPlanApi(transport, ready?.csrfToken ?? null),
      packageApi: packageRuntime.api,
      equipmentApi: packageRuntime.equipmentApi,
      planMembership: planMembership as NonNullable<
        PackagePageRuntime['planMembership']
      >,
      media: packageRuntime.api.media,
      canReadPackages: canUseCatalogAction(ready?.user, 'package', 'read'),
      canReadEquipment: canUseCatalogAction(ready?.user, 'equipment', 'read'),
      canRead: canUseCatalogAction(ready?.user, 'plan', 'read'),
      canManage: canUseCatalogAction(ready?.user, 'plan', 'edit'),
      canCreate: canUseCatalogAction(ready?.user, 'plan', 'create'),
      canSetStatus: canUseCatalogAction(ready?.user, 'plan', 'set_status'),
      canDelete: canUseCatalogAction(ready?.user, 'plan', 'delete'),
      recoverUnauthorized: refresh,
    }),
    [packageRuntime, planMembership, ready, refresh, transport],
  );
  if (!ready || !planMembership) return null;
  return (
    <PlanPageRuntimeProvider value={value}>{children}</PlanPageRuntimeProvider>
  );
}

export function usePlanPageRuntime(): PlanPageRuntime {
  const value = useContext(Context);
  if (!value) throw new Error('PlanPageRuntimeProvider is required');
  return value;
}
